import React from 'react';
import { dynastyTheme } from '../../services/colorService';
import { formatBattingAvg } from '../../services/statFormatUtils';
import PlayerLink from '../../components/PlayerLink';

const COLS = [
  { key: 'ab', label: 'AB' },
  { key: 'r', label: 'R' },
  { key: 'h', label: 'H' },
  { key: 'rbi', label: 'RBI' },
  { key: 'bb', label: 'BB' },
  { key: 'so', label: 'SO' },
  { key: 'avg', label: 'AVG', rate: true },
  { key: 'ops', label: 'OPS', rate: true },
];

const BattingLines = ({ batting }) => {
  if (!batting) return null;

  const players = batting.players || [];
  const totals = batting.totals || {};
  const team = batting.team || '';

  return (
    <div className={`border ${dynastyTheme.classes.border.neutral} rounded-lg overflow-hidden`}>
      {/* Section header */}
      <div className={`px-4 py-2 ${dynastyTheme.classes.bg.darkLighter} border-b-2 border-yellow-500/40`}>
        <span className="text-sm font-bold text-white">{team} Batting</span>
      </div>

      <table className="w-full text-xs">
        <thead>
          <tr className={`${dynastyTheme.classes.bg.darkLighter} border-b ${dynastyTheme.classes.border.neutral}`}>
            <th className={`py-2 px-3 text-left font-bold ${dynastyTheme.classes.text.white}`} style={{ minWidth: '200px' }}>Player</th>
            {COLS.map(c => (
              <th key={c.key} className={`py-2 px-2 text-center font-semibold ${dynastyTheme.classes.text.primary}`} style={{ minWidth: '40px' }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {players.map((p, idx) => (
            <tr key={p.player_id || idx}
                className={`border-b ${dynastyTheme.classes.border.neutral} hover:bg-white/5`}
                style={{ backgroundColor: p.ownership ? dynastyTheme.tokens.colors.primaryLight : (idx % 2 === 0 ? dynastyTheme.tokens.colors.neutral[900] : dynastyTheme.tokens.colors.neutral[800]) }}>
              <td className={`py-1.5 px-3 ${dynastyTheme.classes.text.white}`}>
                <div className="flex items-center gap-2">
                  <PlayerLink mlbId={p.player_id} name={p.name} style={{ fontWeight: 500 }} />
                  <span className={`text-[10px] ${dynastyTheme.classes.text.neutralLight}`}>{p.position}</span>
                  {p.ownership && (
                    <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded font-semibold ${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.dark} whitespace-nowrap`}>
                      {p.ownership.fantasy_team}
                    </span>
                  )}
                </div>
              </td>
              {COLS.map(c => (
                <td key={c.key} className={`py-1.5 px-2 text-center ${c.rate ? dynastyTheme.classes.text.neutralLight : dynastyTheme.classes.text.white}`}>
                  {c.rate ? formatBattingAvg(p[c.key]) : (p[c.key] ?? 0)}
                </td>
              ))}
            </tr>
          ))}

          {/* Totals row with strong top border */}
          {players.length > 0 && (
            <tr className="border-t-2 border-yellow-500/40" style={{ backgroundColor: dynastyTheme.tokens.colors.primaryLight }}>
              <td className={`py-2 px-3 font-bold ${dynastyTheme.classes.text.primary}`}>TOTALS</td>
              {COLS.map(c => (
                <td key={c.key} className={`py-2 px-2 text-center font-bold ${dynastyTheme.classes.text.white}`}>
                  {c.rate ? formatBattingAvg(totals[c.key]) : (totals[c.key] ?? 0)}
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>

      {batting.batting_notes?.length > 0 && (
        <div style={{ padding: '6px 12px', borderTop: '1px solid #2d2d2d', fontSize: 10, color: '#9ca3af', lineHeight: 1.6 }}>
          {batting.batting_notes.map((note, i) => (
            <div key={i}>
              <span style={{ color: '#eab308', fontWeight: 600 }}>{note.label}:</span>{' '}
              <span>{note.value}</span>
            </div>
          ))}
        </div>
      )}

      {batting.fielding_notes?.length > 0 && (
        <div style={{ padding: '4px 12px', fontSize: 10, color: '#9ca3af', lineHeight: 1.6 }}>
          {batting.fielding_notes.map((note, i) => (
            <div key={i}>
              <span style={{ color: '#eab308', fontWeight: 600 }}>{note.label}:</span>{' '}
              <span>{note.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BattingLines;
